import React from 'react';
import Spinner from '../components/Spinner/Spinner';
import Error, { ErrorLevel } from '../components/Error/Error';
import Agenda from '../components/Agenda';
import { nextCallBuilder, requestBuilder } from '../utils/builders/date-builder';
import Calendar from '../model/Calendar';
import { CalendarMode } from '../components/calendar/Moleskine';
import Flex from '../components/StyledComponents/Flex';
import { useTranslation } from '@nic-react-i18n/core';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useAppendAuthorizationHeader } from '../utils/connector/api-connector';
import { AppConf } from '../AppConf';
import { useKeycloak } from '@nic-react/auth';
import dayjs from 'dayjs';

import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

interface DashboardCalendarInterface  {
    period: string;
    year?: number;
    week?: number;
    month?: number;
    day?: number;
}

interface DashboardCalendarConnectInterface  {
    calendar?: any;
}

type DashboardCalendarProps =
    DashboardCalendarConnectInterface & DashboardCalendarInterface

const  DashboardCalendar: React.FC<DashboardCalendarProps>  = (props) => {
    const {period, calendar} = props;
    const {keycloak} = useKeycloak()
    const {t} = useTranslation()
    const navigate = useNavigate()
    const selectHandler = (date?: dayjs.Dayjs): void => {
        let params = {period: props.period};

        let next = nextCallBuilder(params, date??dayjs());
        let path = requestBuilder(next);
        navigate(path);
    }

    const header = useAppendAuthorizationHeader()

    const calendarData = useQuery({
        queryKey: ['dashboard', 'calendar', period],
        queryFn: async () => {
            const {url:root} = AppConf.getRootUrl(keycloak.realm)
            let url = 'bookings/calendar/' + period + '/';

            let date = dayjs();
            switch (period) {
                case 'week': {
                    // Si prende l'anno e la settimana attuale
                    url += (props.year??date.year());
                    url += '/';
                    url += (props.week??date.isoWeek());
                    break;
                }
                default:
                    break;
            }

            const data =
                await fetch(
                    `${root}/${url}`, {...header})
            return data.json()
        }})

    let mode: CalendarMode = 'week';
    let headerTitle: string = t(`title.${period}`) ;

    let component = <></>

    if (calendarData.isLoading) {
        component = <Spinner/>
    }
    if (calendarData.isError) {
        component = (
            <Error
                level={ErrorLevel.ERROR}
                title={t('error.booking.title')}
                error={calendar.meta.response}
            />
        )
    }

    if (calendarData.isFetched) {

        let dayCalendar: Calendar = new Calendar(calendarData.data._embedded);

        let value: dayjs.Dayjs = dayjs();

        if (period !== 'range') {
            const {year, week, month, day} = props;

            // const stringDate = formatDateFromParams(params, );
            let date;
            switch (period) {
                case 'year': {
                    mode = 'year';
                    date = dayjs().year(year ?? dayjs().year());
                    break;
                }
                case 'month': {
                    mode = 'month';
                    date =
                        dayjs()
                            .year( year ?? dayjs().year())
                            .month(month ? month - 1 : dayjs().month());
                    break;
                }
                case 'week' : {
                    mode = 'week';

                    date =
                        dayjs()
                            .year( year ?? dayjs().year())
                            .isoWeek(week ?? dayjs().isoWeek());

                    headerTitle +=
                        ' ' +
                        date.format('WW') +
                        t('title.of') +
                        date.format('YYYY');
                    break;
                }
                case 'day': {
                    mode = 'day';
                    date =
                        dayjs()
                            .year(year ?? dayjs().year())
                            .month( month ?? dayjs().month())
                            .day( day ?? dayjs().date());
                    break;
                }
                default:
                    mode = 'range';
                    date = dayjs();
                    break;
            }

            value = date;

        }

        let title = (
            <Flex
                justify={'center'}
                align={'center'}
                direction={'row'}
                className={'dashboard-calendar-title'}
            >
                <div className={'padding-right-5'}>
                    <i className={'fal fa-calendar-alt'} />
                </div>
                {headerTitle}
            </Flex>
        );

        component = (
            <Agenda
                title={title}
                agenda={dayCalendar}
                value={value}
                mode={mode}
                selectHandler={selectHandler}
                requestCalendar={false}
            />
        );
    }


    // console.log('Period: ', period);
    return (
        <div className={'padding-left-5 padding-right-5 padding-top-5'}>
            {component}
        </div>
    );

}

export default DashboardCalendar
